import React, { useMemo } from 'react'
import { IPlacementContext, PlacementContext } from '@contexts/placement-context'
import { SectionContext } from '@contexts/section-context'
import { SectionThemeProvider } from '@contexts/section-theme-context'
import { useSectionTheme } from '@lib/hooks/use-section-theme'
import { PageSwoosh } from '@lib/types'
import { isTypeSectionTrustedBy } from '@lib/types/content-types'
import { AnySection } from '@lib/types/section'
import { GenericContentRendererProps } from '../types'

interface SectionRendererProps {
  isHero?: boolean
  isH1Section?: boolean
  isGrouped?: boolean
  hasGroupHeading?: boolean
  sectionGroupSwoosh?: PageSwoosh
  structure?: AnySection | AnySection[]
  startIndexInPage?: number
  isTrustedBySection?: boolean
  isReducedHeroPadding?: boolean
}

export const buildSectionRenderer = (
  ContentRenderer: React.FC<GenericContentRendererProps>
): React.FC<SectionRendererProps> => {
  const SectionRenderer = (props: SectionRendererProps) => {
    const { structure, startIndexInPage, isReducedHeroPadding, ...rest } = props

    if (!structure) {
      return null
    }

    if (Array.isArray(structure)) {
      return (
        <>
          {structure.map((s, i) => {
            // Guard if the linked entity is broken
            if (!s.sys?.contentType?.sys) {
              return null
            }
            let nextStartIndexInPage
            if (startIndexInPage !== undefined) {
              nextStartIndexInPage = rest.isGrouped ? startIndexInPage : startIndexInPage + i
            }

            return (
              <SectionRenderer
                key={`${s.sys.contentType.sys.id}-${s.sys.id}`}
                {...rest}
                startIndexInPage={nextStartIndexInPage}
                structure={s}
                isTrustedBySection={isTypeSectionTrustedBy(s)}
              />
            )
          })}
        </>
      )
    }

    return (
      <Section
        {...rest}
        structure={structure}
        indexInPage={startIndexInPage}
        isReducedHeroPadding={isReducedHeroPadding}
      >
        <ContentRenderer structure={structure} />
      </Section>
    )
  }

  return SectionRenderer
}

interface SectionProps {
  isHero?: boolean
  isH1Section?: boolean
  indexInPage?: number
  isGrouped?: boolean
  hasGroupHeading?: boolean
  sectionGroupSwoosh?: PageSwoosh
  structure: AnySection
  isTrustedBySection?: boolean
  children?: React.ReactNode
  isReducedHeroPadding?: boolean
}

const Section: React.FC<SectionProps> = ({
  isHero,
  isH1Section,
  indexInPage,
  isGrouped,
  hasGroupHeading,
  structure,
  sectionGroupSwoosh,
  isTrustedBySection,
  children,
  isReducedHeroPadding,
}) => {
  const { internalName, background } = structure.fields || {}
  const placementContext = useMemo<IPlacementContext>(
    () => ({ placement: 'Section', placementInternalName: internalName }),
    [internalName]
  )
  const themeValue = useSectionTheme(background?.color)

  return (
    <SectionContext.Provider
      value={{
        isHero,
        isH1Section,
        indexInPage,
        isGrouped,
        hasGroupHeading,
        sectionGroupSwoosh,
        isTrustedBySection,
        isReducedHeroPadding,
      }}
    >
      <SectionThemeProvider value={themeValue}>
        <PlacementContext.Provider value={placementContext}>{children}</PlacementContext.Provider>
      </SectionThemeProvider>
    </SectionContext.Provider>
  )
}
